import { RecurlyPlanCode, PlanFeatureConditions } from '~/types/plan';

export interface Feature {
  title: string;
  description?: string;
  includedOn: RecurlyPlanCode[];
  additionalPlanFeatureConditions: PlanFeatureConditions[];
}
export interface FeatureContents {
  resourceBundles: Feature[];
  advancedTools: Feature[];
  resourceFormats: Feature[];
  downloads?: Feature[];
}
export interface PlanFeatures {
  au: FeatureContents;
  us: FeatureContents;
  gb: FeatureContents;
}

export const planFeatures: PlanFeatures = {
  au: {
    advancedTools: [
      {
        title: 'Widgets',
        description:
          'Bring student engagement to new heights with educational digital games PLUS handy digital apps and creator tools to take the hassle out of common classroom tasks.',
        includedOn: ['plus_monthly', 'plus_annual'],
        additionalPlanFeatureConditions: ['free_limited', 'essential_limited'],
      },
      {
        title: 'Word Lists',
        description:
          'Take basic vocabulary lists to the next level. Easily turn your lists into word searches, games and so much more.',
        includedOn: ['plus_monthly', 'plus_annual'],
        additionalPlanFeatureConditions: [],
      },
      {
        title: 'Studio',
        description:
          'Put your own touch on resources with an easy-to-navigate customisation tool for creating classroom signs, designing labels and more!',
        includedOn: ['plus_monthly', 'plus_annual'],
        additionalPlanFeatureConditions: ['free_view', 'essential_view'],
      },
      {
        title: 'Folders and Bookmarks',
        description:
          'Lesson plan on the go with mobile resource bookmarking and resource management folders designed to help you get organised and stay that way all school year long.',
        includedOn: ['free', 'essential_annual', 'plus_monthly', 'plus_annual'],
        additionalPlanFeatureConditions: [],
      },
    ],
    resourceBundles: [
      {
        title: 'Lesson Plans',
        description:
          'Choose from hundreds of start-to-finish lessons packed full of multiple resources — with differentiation tips and assessment strategies built in.',
        includedOn: ['plus_monthly', 'plus_annual'],
        additionalPlanFeatureConditions: ['free_limited', 'essential_limited'],
      },
      {
        title: 'Unit Plans',
        description:
          'Save time with Unit Plans – multiple lesson plans bundled together to form complete units. Get easy-to-download files, sequencing suggestions and more.',
        includedOn: ['plus_monthly', 'plus_annual'],
        additionalPlanFeatureConditions: [],
      },
      {
        title: 'Classroom Theme Décor Bundles',
        description:
          'Classroom decorating has never been easier! Just pick, print and prep for the new term with classroom theme bundles stocked with the things you need most to create a space where students thrive (and you do to).',
        includedOn: ['plus_monthly', 'plus_annual'],
        additionalPlanFeatureConditions: [],
      },
    ],
    resourceFormats: [
      {
        title: 'Google Suite (Google Slides, Docs, etc.)',
        includedOn: ['plus_monthly', 'plus_annual'],
        additionalPlanFeatureConditions: [],
      },
      {
        title: 'Microsoft Suite (PowerPoint, Word, etc.)',
        includedOn: ['plus_monthly', 'plus_annual'],
        additionalPlanFeatureConditions: [],
      },
      {
        title: 'PDFs (Non-Editable)',
        includedOn: ['essential_annual', 'plus_monthly', 'plus_annual'],
        additionalPlanFeatureConditions: ['free_limited'],
      },
    ],
  },
  us: {
    downloads: [
      {
        title: 'Teaching Resource Downloads',
        description:
          'Explore our huge library, containing thousands of curriculum aligned teaching resources, activities and games.',
        includedOn: ['plus_annual', 'plus_lite', 'plus_monthly', 'free'],
        additionalPlanFeatureConditions: [],
      },
    ],
    advancedTools: [
      {
        title: 'Widgets',
        description:
          'Bring student engagement to new heights with educational digital games PLUS handy digital apps and creator tools to take the hassle out of common classroom tasks.',
        includedOn: ['plus_monthly', 'plus_annual', 'plus_lite'],
        additionalPlanFeatureConditions: ['free_limited', 'essential_limited'],
      },
      {
        title: 'Word Lists',
        description:
          'Take basic vocabulary lists to the next level. Easily turn your lists into word searches, games and so much more.',
        includedOn: ['plus_monthly', 'plus_annual', 'plus_lite'],
        additionalPlanFeatureConditions: [],
      },
      {
        title: 'Folders and Bookmarks',
        description:
          'Lesson plan on the go with mobile resource bookmarking and resource management folders designed to help you get organized and stay that way all school year long.',
        includedOn: ['free', 'essential_annual', 'plus_monthly', 'plus_annual', 'plus_lite'],
        additionalPlanFeatureConditions: [],
      },
    ],
    resourceBundles: [
      {
        title: 'Lesson Plans',
        description:
          'Choose from hundreds of start-to-finish lessons packed full of multiple resources — with differentiation tips and assessment strategies built in.',
        includedOn: ['plus_monthly', 'plus_annual', 'plus_lite'],
        additionalPlanFeatureConditions: ['free_limited', 'essential_limited'],
      },
      {
        title: 'Unit Plans',
        description:
          'Save time with Unit Plans – multiple lesson plans bundled together to form complete units. Get easy-to-download files, sequencing suggestions and more.',
        includedOn: ['plus_monthly', 'plus_annual', 'plus_lite'],
        additionalPlanFeatureConditions: [],
      },
      {
        title: 'Classroom Theme Décor Bundles',
        description:
          'Classroom decorating has never been easier! Just pick, print and prep for the school year with classroom theme bundles stocked with the things you need most to create a space where students thrive (and you do to).',
        includedOn: ['plus_monthly', 'plus_annual', 'plus_lite'],
        additionalPlanFeatureConditions: [],
      },
    ],
    resourceFormats: [
      {
        title: 'Google Suite (Google Slides, Docs, etc.)',
        includedOn: ['plus_monthly', 'plus_annual', 'plus_lite'],
        additionalPlanFeatureConditions: [],
      },
      {
        title: 'Microsoft Suite (PowerPoint, Word, etc.)',
        includedOn: ['plus_monthly', 'plus_annual', 'plus_lite'],
        additionalPlanFeatureConditions: [],
      },
      {
        title: 'PDFs (Non-Editable)',
        includedOn: ['essential_annual', 'plus_monthly', 'plus_annual', 'plus_lite'],
        additionalPlanFeatureConditions: ['free_limited'],
      },
    ],
  },
  gb: {
    advancedTools: [
      {
        title: 'Widgets',
        description:
          'Bring student engagement to new heights with educational digital games PLUS handy digital apps and creator tools to take the hassle out of common classroom tasks.',
        includedOn: ['plus_monthly', 'plus_annual', 'plus_lite'],
        additionalPlanFeatureConditions: ['free_limited', 'essential_limited'],
      },
      {
        title: 'Word Lists',
        description:
          'Take basic vocabulary lists to the next level. Easily turn your lists into word searches, games and so much more.',
        includedOn: ['plus_monthly', 'plus_annual', 'plus_lite'],
        additionalPlanFeatureConditions: [],
      },
      {
        title: 'Studio',
        description:
          'Put your own touch on resources with an easy-to-navigate customisation tool for creating classroom signs, designing labels and more!',
        includedOn: ['plus_monthly', 'plus_annual', 'plus_lite'],
        additionalPlanFeatureConditions: ['free_view', 'essential_view'],
      },
      {
        title: 'Folders and Bookmarks',
        description:
          'Lesson plan on the go with mobile resource bookmarking and resource management folders designed to help you get organised and stay that way all school year long.',
        includedOn: ['free', 'essential_annual', 'plus_monthly', 'plus_annual', 'plus_lite'],
        additionalPlanFeatureConditions: [],
      },
    ],
    resourceBundles: [
      {
        title: 'Lesson Plans',
        description:
          'Choose from hundreds of start-to-finish lessons packed full of multiple resources — with differentiation tips and assessment strategies built in.',
        includedOn: ['plus_monthly', 'plus_annual', 'plus_lite'],
        additionalPlanFeatureConditions: ['free_limited', 'essential_limited'],
      },
      {
        title: 'Unit Plans',
        description:
          'Save time with Unit Plans – multiple lesson plans bundled together to form complete units. Get easy-to-download files, sequencing suggestions and more.',
        includedOn: ['plus_monthly', 'plus_annual', 'plus_lite'],
        additionalPlanFeatureConditions: [],
      },
      {
        title: 'Classroom Theme Décor Bundles',
        description:
          'Classroom decorating has never been easier! Just pick, print and prep for the new term with classroom theme bundles stocked with the things you need most to create a space where students thrive (and you do to).',
        includedOn: ['plus_monthly', 'plus_annual', 'plus_lite'],
        additionalPlanFeatureConditions: [],
      },
    ],
    resourceFormats: [
      {
        title: 'Google Suite (Google Slides, Docs, etc.)',
        includedOn: ['plus_monthly', 'plus_annual', 'plus_lite'],
        additionalPlanFeatureConditions: [],
      },
      {
        title: 'Microsoft Suite (PowerPoint, Word, etc.)',
        includedOn: ['plus_monthly', 'plus_annual', 'plus_lite'],
        additionalPlanFeatureConditions: [],
      },
      {
        title: 'PDFs (Non-Editable)',
        includedOn: ['essential_annual', 'plus_monthly', 'plus_annual', 'plus_lite'],
        additionalPlanFeatureConditions: ['free_limited'],
      },
    ],
  },
};
